import { useState, useRef } from 'react';
import { Camera, Loader2, Volume2, VolumeX, RotateCcw, Sparkles, X, ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
    { code: 'en', name: 'English' }, { code: 'it', name: 'Italiano' },
    { code: 'es', name: 'Espanol' }, { code: 'fr', name: 'Francais' },
    { code: 'de', name: 'Deutsch' }, { code: 'pt', name: 'Portugues' },
    { code: 'ja', name: 'Japanese' }, { code: 'zh', name: 'Chinese' },
    { code: 'ko', name: 'Korean' }, { code: 'ar', name: 'Arabic' },
];

export default function AIVisualGuide({ language = 'en', onClose }) {
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [result, setResult] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [lang, setLang] = useState(language);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const audioRef = useRef(null);

    const handleImage = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }

        const reader = new FileReader();
        reader.onload = (ev) => {
            setImagePreview(ev.target.result);
            setImage(ev.target.result);
            setResult(null);
        };
        reader.readAsDataURL(file);
    };

    const analyze = async () => {
        if (!image) { toast.error('Please take or upload a photo first'); return; }
        setAnalyzing(true);
        setResult(null);
        try {
            const res = await fetch(`${API}/api/ai/visual-guide`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ image, language: lang, question: 'What is this? Tell me everything about it.' })
            });
            if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Analysis failed'); }
            const data = await res.json();
            setResult(data);
            // Auto-play audio if available
            if (data.audio_base64) {
                playAudio(data.audio_base64);
            }
        } catch (err) {
            toast.error(err.message);
        } finally {
            setAnalyzing(false);
        }
    };

    const playAudio = (audioB64) => {
        if (audioRef.current) { audioRef.current.pause(); }
        const audio = new Audio(`data:audio/mp3;base64,${audioB64}`);
        audioRef.current = audio;
        audio.onplay = () => setPlaying(true);
        audio.onended = () => setPlaying(false);
        audio.onerror = () => { setPlaying(false); toast.error('Audio playback failed'); };
        audio.play().catch(() => setPlaying(false));
    };

    const stopAudio = () => {
        if (audioRef.current) { audioRef.current.pause(); setPlaying(false); }
    };

    const reset = () => {
        stopAudio();
        setImage(null);
        setImagePreview(null);
        setResult(null);
    };

    return (
        <div className="flex flex-col h-full" data-testid="ai-visual-guide">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-gold" />
                    <span className="text-sm font-semibold text-white">AI Visual Guide</span>
                </div>
                <div className="flex items-center gap-2">
                    <select value={lang} onChange={e => setLang(e.target.value)}
                        className="text-xs bg-white/5 border border-white/10 text-white rounded-lg px-2 py-1 focus:outline-none" data-testid="visual-guide-lang">
                        {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
                    </select>
                    {onClose && <button onClick={onClose} className="p-1 text-white/40 hover:text-white"><X className="w-4 h-4" /></button>}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Image capture area */}
                {!imagePreview ? (
                    <div className="flex flex-col items-center gap-4 py-8">
                        <div className="w-20 h-20 rounded-2xl bg-gold/10 flex items-center justify-center">
                            <Camera className="w-10 h-10 text-gold" />
                        </div>
                        <div className="text-center">
                            <p className="text-white font-medium mb-1">Point & Discover</p>
                            <p className="text-white/40 text-xs max-w-[240px]">Take a photo of any painting, monument, statue, or building and AI will tell you all about it</p>
                        </div>
                        <div className="flex gap-3">
                            <Button onClick={() => cameraInputRef.current?.click()} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="take-photo-btn">
                                <Camera className="w-4 h-4" /> Take Photo
                            </Button>
                            <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="border-white/10 text-white/60 gap-2" data-testid="upload-photo-btn">
                                <ImageIcon className="w-4 h-4" /> Upload
                            </Button>
                        </div>
                        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleImage} className="hidden" />
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImage} className="hidden" />
                    </div>
                ) : (
                    <>
                        {/* Image preview */}
                        <div className="relative rounded-xl overflow-hidden border border-white/10">
                            <img src={imagePreview} alt="Captured" className="w-full max-h-[200px] object-cover" />
                            <button onClick={reset} className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1.5 hover:bg-black" data-testid="reset-photo-btn">
                                <RotateCcw className="w-3.5 h-3.5" />
                            </button>
                        </div>

                        {/* Analyze button */}
                        {!result && !analyzing && (
                            <Button onClick={analyze} className="w-full bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="analyze-btn">
                                <Sparkles className="w-4 h-4" /> Identify & Explain
                            </Button>
                        )}

                        {/* Loading */}
                        {analyzing && (
                            <div className="flex flex-col items-center gap-3 py-6">
                                <Loader2 className="w-8 h-8 text-gold animate-spin" />
                                <p className="text-white/40 text-sm">Analyzing image...</p>
                            </div>
                        )}

                        {/* Result */}
                        {result && (
                            <div className="space-y-3" data-testid="analysis-result">
                                <div className="p-4 rounded-xl bg-gradient-to-b from-white/[0.04] to-transparent border border-white/[0.08]">
                                    <div className="prose prose-invert prose-sm max-w-none text-white/80 text-sm leading-relaxed whitespace-pre-wrap">
                                        {result.analysis}
                                    </div>
                                </div>

                                {/* Audio controls */}
                                <div className="flex items-center gap-2">
                                    {result.audio_base64 ? (
                                        <Button onClick={() => playing ? stopAudio() : playAudio(result.audio_base64)}
                                            variant="outline" size="sm" className={`gap-2 ${playing ? 'border-gold/30 text-gold' : 'border-white/10 text-white/60'}`} data-testid="play-audio-btn">
                                            {playing ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                                            {playing ? 'Stop' : 'Listen'}
                                        </Button>
                                    ) : result.audio_error ? (
                                        <span className="text-[10px] text-white/30">{result.audio_error}</span>
                                    ) : null}
                                    <Button onClick={reset} variant="outline" size="sm" className="border-white/10 text-white/60 gap-2" data-testid="new-scan-btn">
                                        <Camera className="w-3.5 h-3.5" /> New Scan
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
